// src/pages/auth/RegisterPage.tsx
// Registration page matching stitch corde_login_and_registration design.

import { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useAuth, useI18n } from '@react/hooks';
import { ErrorAlert } from '@react/components';
import { validateSignUp, calculatePasswordStrength, passwordStrengthLabels } from '../../features/authn/logic';
import type { SignUpFormState } from '../../features/authn/types';
import { PasswordStrength } from '../../components/PasswordStrength';
import { AuthLayout } from './AuthLayout';

export const RegisterPage = observer(() => {
  const auth = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();

  const [form, setForm] = useState<SignUpFormState>({
    email: '',
    username: '',
    password: '',
    passwordConfirmation: '',
    referralCode: '',
    acceptTerms: false,
  });
  const [errors, setErrors] = useState<Record<string, unknown>>({});
  const [showPassword, setShowPassword] = useState(false);

  if (auth.isAuthenticated) return <Navigate to="/dashboard" replace />;

  const passwordScore = calculatePasswordStrength(form.password);

  const handleChange = (field: keyof SignUpFormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: val }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validation = validateSignUp(form);
    if (Object.keys(validation).length > 0) {
      setErrors(Object.fromEntries(Object.entries(validation).map(([k, v]) => [k, t(v)])));
      return;
    }

    try {
      await auth.register({
        email: form.email,
        username: form.username,
        password: form.password,
        password_confirmation: form.passwordConfirmation,
        referral_code: form.referralCode || '',
      });
      navigate('/login', {
        replace: true,
        state: { flash: t('auth.registerSuccess'), email: form.email },
      });
    } catch (err) {
      setErrors({ form: err });
    }
  };

  return (
    <AuthLayout>
      {/* Auth card */}
      <div className="w-full max-w-[480px] mx-auto bg-white dark:bg-zinc-900/50 backdrop-blur-xl border border-slate-200 dark:border-zinc-800 rounded-2xl shadow-2xl overflow-hidden">
        {/* Tab header */}
        <div className="flex border-b border-slate-200 dark:border-zinc-800">
          <Link
            to="/login"
            className="flex-1 py-4 text-sm font-bold text-center border-b-2 border-transparent text-slate-500 dark:text-zinc-500 hover:text-slate-700 dark:hover:text-zinc-300 transition-colors"
          >
            {t('auth.login')}
          </Link>
          <button className="flex-1 py-4 text-sm font-bold border-b-2 border-primary text-slate-900 dark:text-white transition-colors">
            {t('auth.register')}
          </button>
        </div>

        <div className="p-8">
          {/* Welcome section */}
          <div className="mb-8">
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">{t('auth.registerSubtitle')}</h2>
            <p className="text-sm text-slate-500 dark:text-zinc-400">
              Fill in the details below to get started with your organization.
            </p>
          </div>

          {errors.form ? (
            <div className="mb-5">
              <ErrorAlert error={errors.form} dismissible onDismiss={() => setErrors((p) => ({ ...p, form: null }))} />
            </div>
          ) : null}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 dark:text-zinc-300 mb-1.5" htmlFor="reg-email">
                {t('auth.email')}
              </label>
              <div className="relative">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 size-5 text-slate-400 dark:text-zinc-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <input
                  id="reg-email"
                  type="email"
                  value={form.email}
                  onChange={handleChange('email')}
                  placeholder="alex@company.com"
                  autoComplete="email"
                  className="w-full pl-10 pr-4 py-3 bg-slate-50 dark:bg-zinc-950 border border-slate-200 dark:border-zinc-800 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-zinc-600"
                />
              </div>
              {errors.email ? <p className="mt-1 text-xs text-red-500">{errors.email as string}</p> : null}
            </div>

            {/* Username */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 dark:text-zinc-300 mb-1.5" htmlFor="reg-username">
                {t('auth.username')}
              </label>
              <div className="relative">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 size-5 text-slate-400 dark:text-zinc-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <input
                  id="reg-username"
                  type="text"
                  value={form.username}
                  onChange={handleChange('username')}
                  placeholder={t('auth.usernamePlaceholder')}
                  autoComplete="username"
                  className="w-full pl-10 pr-4 py-3 bg-slate-50 dark:bg-zinc-950 border border-slate-200 dark:border-zinc-800 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-zinc-600"
                />
              </div>
              {errors.username ? <p className="mt-1 text-xs text-red-500">{errors.username as string}</p> : null}
            </div>

            {/* Password + Confirm password */}
            <div className="space-y-1">
              <div>
                <label className="block text-sm font-semibold text-slate-700 dark:text-zinc-300 mb-1.5" htmlFor="reg-password">
                  {t('auth.password')}
                </label>
                <div className="relative">
                  <svg className="absolute left-3 top-1/2 -translate-y-1/2 size-5 text-slate-400 dark:text-zinc-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  <input
                    id="reg-password"
                    type={showPassword ? 'text' : 'password'}
                    value={form.password}
                    onChange={handleChange('password')}
                    placeholder="••••••••"
                    autoComplete="new-password"
                    className="w-full pl-10 pr-12 py-3 bg-slate-50 dark:bg-zinc-950 border border-slate-200 dark:border-zinc-800 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-zinc-600"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-zinc-500 hover:text-primary transition-colors"
                  >
                    {showPassword ? (
                      <svg className="size-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                    ) : (
                      <svg className="size-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                    )}
                  </button>
                </div>
                {errors.password ? <p className="mt-1 text-xs text-red-500">{errors.password as string}</p> : null}
                <div className={`mt-2${form.password ? '' : ' invisible'}`}>
                  <PasswordStrength score={passwordScore} label={t(passwordStrengthLabels[passwordScore])} />
                </div>
              </div>

              {/* Confirm password */}
              <div>
              <label className="block text-sm font-semibold text-slate-700 dark:text-zinc-300 mb-1.5" htmlFor="reg-confirm">
                {t('auth.confirmPassword')}
              </label>
              <div className="relative">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 size-5 text-slate-400 dark:text-zinc-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <input
                  id="reg-confirm"
                  type="password"
                  value={form.passwordConfirmation}
                  onChange={handleChange('passwordConfirmation')}
                  placeholder="••••••••"
                  autoComplete="new-password"
                  className="w-full pl-10 pr-4 py-3 bg-slate-50 dark:bg-zinc-950 border border-slate-200 dark:border-zinc-800 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-zinc-600"
                />
              </div>
              {errors.passwordConfirmation ? <p className="mt-1 text-xs text-red-500">{errors.passwordConfirmation as string}</p> : null}
              </div>
            </div>

            {/* Referral code */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 dark:text-zinc-300 mb-1.5" htmlFor="reg-referral">
                {t('auth.referralCode')}
              </label>
              <input
                id="reg-referral"
                type="text"
                value={form.referralCode}
                onChange={handleChange('referralCode')}
                placeholder={t('auth.referralCodePlaceholder')}
                className="w-full px-4 py-3 bg-slate-50 dark:bg-zinc-950 border border-slate-200 dark:border-zinc-800 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-zinc-600"
              />
            </div>

            {/* Terms */}
            <div className="flex items-start gap-2">
              <input
                id="accept-terms"
                type="checkbox"
                checked={form.acceptTerms}
                onChange={handleChange('acceptTerms')}
                className="w-4 h-4 mt-0.5 rounded border-slate-300 dark:border-zinc-800 text-primary focus:ring-primary bg-slate-50 dark:bg-zinc-950"
              />
              <label htmlFor="accept-terms" className="text-sm text-slate-600 dark:text-zinc-400 select-none">
                {t('auth.agreeToTerms')}{' '}
                <Link to="/terms" className="text-primary hover:underline font-medium">{t('auth.termsOfService')}</Link>
                {' '}{t('common.and')}{' '}
                <Link to="/privacy" className="text-primary hover:underline font-medium">{t('auth.privacyPolicy')}</Link>
              </label>
            </div>
            {errors.acceptTerms ? <p className="text-xs text-red-500">{errors.acceptTerms as string}</p> : null}

            {/* Submit button */}
            <button
              type="submit"
              disabled={auth.isLoading}
              className="w-full py-3.5 bg-primary hover:bg-primary/90 disabled:opacity-60 text-white font-bold rounded-xl shadow-lg shadow-primary/20 transition-all flex items-center justify-center gap-2"
            >
              {auth.isLoading ? (
                <svg className="animate-spin size-5" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>
              ) : (
                <>
                  {t('auth.registerButton')}
                  <svg className="size-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
                </>
              )}
            </button>
          </form>
        </div>

        {/* Card footer — security badge */}
        <div className="p-6 bg-slate-50 dark:bg-zinc-900/80 border-t border-slate-200 dark:border-zinc-800 text-center">
          <p className="text-xs text-slate-500 dark:text-zinc-500 flex items-center justify-center gap-1">
            <svg className="size-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
            Protected by Corde Security Infrastructure
          </p>
        </div>
      </div>
    </AuthLayout>
  );
});

export default RegisterPage;
